/**
 * ChatWidget
 * Flytande chat-komponent som alltid är synlig i nedre högra hörnet
 * Innehåller: Support, Intern chat, Broadcasts
 */

import React from 'react';
import { useTranslation } from '@/store/languageStore';
import { useAuth } from '@/store/authStore';
import {
  XMarkIcon,
  PaperAirplaneIcon,
  QuestionMarkCircleIcon,
  UserGroupIcon,
  MegaphoneIcon,
  BookOpenIcon,
  ChevronLeftIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconSolid } from '@heroicons/react/24/solid';
import { useChatWidget } from '@/hooks/useChatWidget';
import FaqTab from './FaqTab';
import { STATUS_COLORS, PRIORITY_COLORS, BROADCAST_PRIORITY_COLORS, type SupportTicket } from '@/types/chat';

// Status badge component
const StatusBadge: React.FC<{ status: SupportTicket['status']; t: (key: string) => string }> = ({ status, t }) => {
  const labels = {
    open: t('chat.statusOpen') || 'Öppen',
    in_progress: t('chat.statusInProgress') || 'Pågår',
    waiting_customer: t('chat.statusWaiting') || 'Väntar svar',
    resolved: t('chat.statusResolved') || 'Löst',
    closed: t('chat.statusClosed') || 'Stängd',
  };

  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status]}`}>
      {labels[status]}
    </span>
  );
};

// Priority badge component
const PriorityBadge: React.FC<{ priority: SupportTicket['priority'] }> = ({ priority }) => {
  if (priority === 'low' || priority === 'medium') return null;
  
  return (
    <ExclamationCircleIcon className={`w-4 h-4 ${PRIORITY_COLORS[priority]}`} />
  );
};

const ChatWidget: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  
  const {
    isOpen,
    activeTab,
    setActiveTab,
    loading,
    position,
    isDragging,
    panelPosition,
    handleDragStart,
    handleButtonClick,
    buttonRef,
    supportView,
    setSupportView,
    tickets,
    selectedTicket,
    setSelectedTicket,
    newTicketSubject,
    setNewTicketSubject,
    newTicketMessage,
    setNewTicketMessage,
    ticketReply,
    setTicketReply,
    handleCreateTicket,
    handleTicketReply,
    handleCloseTicket,
    chatView,
    setChatView,
    conversations,
    chatUsers,
    selectedChatUser,
    chatMessages,
    newChatMessage,
    setNewChatMessage,
    chatSearch,
    setChatSearch,
    isSearching,
    handleStartChat,
    handleSendChatMessage,
    handleCloseChat,
    broadcasts,
    handleMarkBroadcastRead,
    faqItems,
    unreadTickets,
    unreadChats,
    unreadBroadcasts,
    totalUnread,
    formatDate,
    messagesEndRef,
  } = useChatWidget(t);

  return (
    <>
      {/* Floating Draggable Button */}
      <button
        ref={buttonRef}
        data-guide="header-support"
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        onClick={handleButtonClick}
        style={{ left: position.x, top: position.y, cursor: isDragging ? 'grabbing' : 'grab' }}
        className={`fixed z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-colors duration-300 select-none ${
          isOpen 
            ? 'bg-blue-500 hover:bg-blue-600' 
            : totalUnread > 0
              ? 'bg-green-500 hover:bg-green-600 animate-pulse'
              : 'bg-blue-500 hover:bg-blue-600'
        }`}
      >
        {isOpen ? (
          <XMarkIcon className="w-6 h-6 text-white pointer-events-none" />
        ) : (
          <>
            <ChatBubbleLeftRightIconSolid className={`w-6 h-6 text-white pointer-events-none ${totalUnread > 0 ? 'animate-bounce' : ''}`} />
            {totalUnread > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse pointer-events-none">
                {totalUnread > 9 ? '9+' : totalUnread}
              </span>
            )}
          </>
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div 
          style={{ left: panelPosition.left, top: panelPosition.top }}
          className="fixed z-50 w-96 h-[500px] bg-bgElevated rounded-lg shadow-2xl flex flex-col overflow-hidden border border-border"
        >
          {/* Header */}
          <div className="bg-blue-500 text-white px-4 py-3">
            <h3 className="font-semibold">{t('chat.title') || 'Meddelanden & Support'}</h3>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-border">
            {[
              { id: 'support', icon: QuestionMarkCircleIcon, label: t('chat.support') || 'Support', count: unreadTickets },
              { id: 'chat', icon: UserGroupIcon, label: t('chat.chat') || 'Chat', count: unreadChats },
              { id: 'broadcasts', icon: MegaphoneIcon, label: t('chat.broadcasts') || 'Info', count: unreadBroadcasts },
              { id: 'faq', icon: BookOpenIcon, label: t('chat.faq') || 'FAQ', count: 0 },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 px-4 py-2 text-sm font-medium flex items-center justify-center gap-1 ${
                  activeTab === tab.id
                    ? 'text-blue-500 border-b-2 border-blue-500'
                    : 'text-textSecondary hover:text-textDefault'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count > 0 && (
                  <span className="ml-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <>
                {/* Support Tab */}
                {activeTab === 'support' && (
                  <div className="h-full flex flex-col">
                    {supportView === 'list' && (
                      <>
                        <button
                          onClick={() => setSupportView('new')}
                          className="m-3 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium"
                        >
                          + {t('chat.newTicket') || 'Nytt ärende'}
                        </button>
                        <div className="flex-1 overflow-y-auto">
                          {tickets.length === 0 ? (
                            <p className="text-center text-textSecondary py-8 text-sm">
                              {t('chat.noTickets') || 'Inga ärenden'}
                            </p>
                          ) : (
                            <div className="divide-y divide-border">
                              {tickets.map(ticket => (
                                <button
                                  key={ticket.ticket_id}
                                  onClick={() => { setSelectedTicket(ticket); setSupportView('ticket'); }}
                                  className="w-full p-3 text-left hover:bg-bgBase"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <PriorityBadge priority={ticket.priority} />
                                        <span className="font-medium text-textDefault text-sm truncate">
                                          {ticket.subject}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2 mt-1">
                                        <StatusBadge status={ticket.status} t={t} />
                                        <span className="text-xs text-textSecondary">
                                          {formatDate(ticket.updated_at)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {supportView === 'new' && (
                      <div className="p-4 flex flex-col h-full">
                        <button onClick={() => setSupportView('list')} className="flex items-center text-sm text-textSecondary hover:text-textDefault mb-4">
                          <ChevronLeftIcon className="w-4 h-4 mr-1" />
                          {t('common.back') || 'Tillbaka'}
                        </button>
                        <h4 className="font-medium text-textDefault mb-4">{t('chat.newTicket') || 'Nytt supportärende'}</h4>
                        <input
                          type="text"
                          value={newTicketSubject}
                          onChange={e => setNewTicketSubject(e.target.value)}
                          placeholder={t('chat.subject') || 'Ämne'}
                          className="w-full px-3 py-2 border border-border rounded-lg mb-3 text-sm bg-bgBase text-textDefault"
                        />
                        <textarea
                          value={newTicketMessage}
                          onChange={e => setNewTicketMessage(e.target.value)}
                          placeholder={t('chat.message') || 'Beskriv ditt ärende...'}
                          className="w-full flex-1 px-3 py-2 border border-border rounded-lg mb-3 text-sm resize-none bg-bgBase text-textDefault"
                        />
                        <button onClick={handleCreateTicket} className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium">
                          {t('chat.send') || 'Skicka'}
                        </button>
                      </div>
                    )}

                    {supportView === 'ticket' && selectedTicket && (
                      <div className="flex flex-col h-full">
                        <div className="p-3 border-b border-border">
                          <button onClick={() => { setSupportView('list'); setSelectedTicket(null); }} className="flex items-center text-sm text-textSecondary hover:text-textDefault mb-2">
                            <ChevronLeftIcon className="w-4 h-4 mr-1" />
                            {t('common.back') || 'Tillbaka'}
                          </button>
                          <h4 className="font-medium text-textDefault text-sm">{selectedTicket.subject}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <StatusBadge status={selectedTicket.status} t={t} />
                            <span className="text-xs text-textSecondary">{selectedTicket.ticket_id}</span>
                          </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-3 space-y-3">
                          {selectedTicket.messages.map(msg => (
                            <div key={msg.message_id} className={`flex ${msg.is_support_reply ? 'justify-start' : 'justify-end'}`}>
                              <div className={`max-w-[80%] p-3 rounded-lg text-sm ${msg.is_support_reply ? 'bg-bgBase text-textDefault' : 'bg-blue-500 text-white'}`}>
                                {msg.is_support_reply && msg.from_user_name && (
                                  <p className="text-xs font-medium mb-1 text-textSecondary">{msg.from_user_name}</p>
                                )}
                                <p className="whitespace-pre-wrap">{msg.message}</p>
                                <p className={`text-xs mt-1 ${msg.is_support_reply ? 'text-textSecondary' : 'text-blue-200'}`}>{formatDate(msg.created_at)}</p>
                              </div>
                            </div>
                          ))}
                          <div ref={messagesEndRef} />
                        </div>
                        {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' && (
                          <div className="p-3 border-t border-border">
                            <div className="flex gap-2 mb-2">
                              <input
                                type="text"
                                value={ticketReply}
                                onChange={e => setTicketReply(e.target.value)}
                                onKeyPress={e => e.key === 'Enter' && handleTicketReply()}
                                placeholder={t('chat.typeReply') || 'Skriv svar...'}
                                className="flex-1 px-3 py-2 border border-border rounded-lg text-sm bg-bgBase text-textDefault"
                              />
                              <button onClick={handleTicketReply} disabled={!ticketReply.trim()} className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed">
                                <PaperAirplaneIcon className="w-4 h-4" />
                              </button>
                            </div>
                            <button onClick={handleCloseTicket} className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-bgBase text-textDefault rounded-lg hover:bg-bgBase text-sm">
                              <CheckCircleIcon className="w-4 h-4" />
                              {t('chat.closeTicket') || 'Markera som löst'}
                            </button>
                          </div>
                        )}
                        {(selectedTicket.status === 'closed' || selectedTicket.status === 'resolved') && (
                          <div className="p-3 border-t border-border text-center">
                            <span className="text-sm text-textSecondary">{t('chat.ticketIsClosed') || 'Detta ärende är avslutat'}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Chat Tab */}
                {activeTab === 'chat' && (
                  <div className="h-full flex flex-col">
                    {chatView === 'list' && (
                      <>
                        <button onClick={() => setChatView('users')} className="m-3 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium">
                          + {t('chat.newConversation') || 'Ny konversation'}
                        </button>
                        <div className="flex-1 overflow-y-auto">
                          {conversations.length === 0 ? (
                            <div className="p-4 text-center text-textSecondary">
                              <UserGroupIcon className="w-12 h-12 mx-auto mb-3 text-textTertiary" />
                              <p className="text-sm">{t('chat.noConversations') || 'Inga konversationer'}</p>
                              <p className="text-xs mt-1">{t('chat.startConversation') || 'Starta en ny konversation'}</p>
                            </div>
                          ) : (
                            <div className="divide-y divide-border">
                              {conversations.map(conv => (
                                <button
                                  key={conv.conversation_id}
                                  onClick={() => {
                                    const otherUserId = conv.participants.find(p => p !== user?.id);
                                    const otherUser = chatUsers.find(u => u.id === otherUserId);
                                    if (otherUser) handleStartChat({ id: otherUser.id, name: otherUser.name });
                                  }}
                                  className="w-full p-3 text-left hover:bg-bgBase"
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-textDefault text-sm">
                                      {chatUsers.find(u => conv.participants.includes(u.id) && u.id !== user?.id)?.name || 'Okänd'}
                                    </span>
                                    {conv.unread_count > 0 && (
                                      <span className="w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">{conv.unread_count}</span>
                                    )}
                                  </div>
                                  {conv.last_message && (
                                    <p className="text-xs text-textSecondary truncate mt-1">{conv.last_message.message}</p>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {chatView === 'users' && (
                      <div className="flex flex-col h-full">
                        <div className="p-3 border-b border-border">
                          <button onClick={() => { setChatView('list'); setChatSearch(''); }} className="flex items-center text-sm text-textSecondary hover:text-textDefault mb-2">
                            <ChevronLeftIcon className="w-4 h-4 mr-1" />
                            {t('common.back') || 'Tillbaka'}
                          </button>
                          <div className="relative">
                            <input
                              type="text"
                              value={chatSearch}
                              onChange={e => setChatSearch(e.target.value)}
                              placeholder={t('chat.searchUsers') || 'Sök på namn eller e-post...'}
                              autoFocus
                              className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-bgBase text-textDefault pr-8"
                            />
                            {isSearching && (
                              <div className="absolute right-2 top-2">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                              </div>
                            )}
                          </div>
                          {chatSearch.length > 0 && chatSearch.length < 2 && (
                            <p className="text-xs text-textTertiary mt-1">{t('chat.minChars') || 'Skriv minst 2 tecken för att söka'}</p>
                          )}
                        </div>
                        <div className="flex-1 overflow-y-auto">
                          {isSearching ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                              <span className="ml-2 text-sm text-textSecondary">{t('common.searching') || 'Söker...'}</span>
                            </div>
                          ) : chatUsers.length === 0 ? (
                            <div className="text-center text-textSecondary py-8">
                              <UserGroupIcon className="w-10 h-10 mx-auto mb-2 text-textTertiary" />
                              <p className="text-sm">{chatSearch.length >= 2 ? (t('chat.noUsersFound') || 'Inga användare hittades') : (t('chat.searchToFind') || 'Börja skriva för att söka användare')}</p>
                            </div>
                          ) : (
                            <div className="divide-y divide-border">
                              {chatUsers.map(chatUser => (
                                <button key={chatUser.id} onClick={() => handleStartChat({ id: chatUser.id, name: chatUser.name })} className="w-full p-3 text-left hover:bg-bgBase">
                                  <p className="font-medium text-textDefault text-sm">{chatUser.name}</p>
                                  <p className="text-xs text-textSecondary">{chatUser.email}</p>
                                  <p className="text-xs text-blue-500 dark:text-blue-400">{chatUser.organization}</p>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {chatView === 'conversation' && selectedChatUser && (
                      <div className="flex flex-col h-full">
                        <div className="p-3 border-b border-border">
                          <button onClick={() => { setChatView('list'); }} className="flex items-center text-sm text-textSecondary hover:text-textDefault mb-2">
                            <ChevronLeftIcon className="w-4 h-4 mr-1" />
                            {t('common.back') || 'Tillbaka'}
                          </button>
                          <h4 className="font-medium text-textDefault text-sm">{selectedChatUser.name}</h4>
                        </div>
                        <div className="flex-1 overflow-y-auto p-3 space-y-3">
                          {chatMessages.length === 0 ? (
                            <p className="text-center text-textSecondary py-8 text-sm">{t('chat.noMessages') || 'Inga meddelanden ännu'}</p>
                          ) : (
                            chatMessages.map(msg => (
                              <div key={msg.message_id} className={`flex ${msg.from_user_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[80%] p-3 rounded-lg text-sm ${msg.from_user_id === user?.id ? 'bg-blue-500 text-white' : 'bg-bgBase text-textDefault'}`}>
                                  <p className="whitespace-pre-wrap">{msg.message}</p>
                                  <p className={`text-xs mt-1 ${msg.from_user_id === user?.id ? 'text-blue-200' : 'text-textSecondary'}`}>{formatDate(msg.created_at)}</p>
                                </div>
                              </div>
                            ))
                          )}
                          <div ref={messagesEndRef} />
                        </div>
                        <div className="p-3 border-t border-border">
                          <div className="flex gap-2 mb-2">
                            <input
                              type="text"
                              value={newChatMessage}
                              onChange={e => setNewChatMessage(e.target.value)}
                              onKeyPress={e => e.key === 'Enter' && handleSendChatMessage()}
                              placeholder={t('chat.typeMessage') || 'Skriv meddelande...'}
                              className="flex-1 px-3 py-2 border border-border rounded-lg text-sm bg-bgBase text-textDefault"
                            />
                            <button onClick={handleSendChatMessage} disabled={!newChatMessage.trim()} className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed">
                              <PaperAirplaneIcon className="w-4 h-4" />
                            </button>
                          </div>
                          <button onClick={handleCloseChat} className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-bgBase text-textDefault rounded-lg hover:bg-bgBase text-sm">
                            <CheckCircleIcon className="w-4 h-4" />
                            {t('chat.closeConversation') || 'Avsluta konversation'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Broadcasts Tab */}
                {activeTab === 'broadcasts' && (
                  <div className="divide-y divide-border">
                    {broadcasts.length === 0 ? (
                      <p className="text-center text-textSecondary py-8 text-sm">{t('chat.noBroadcasts') || 'Inga meddelanden'}</p>
                    ) : (
                      broadcasts.map(broadcast => {
                        const isRead = broadcast.read_by.includes(user?.id || '');
                        return (
                          <div
                            key={broadcast.broadcast_id}
                            onClick={() => !isRead && handleMarkBroadcastRead(broadcast.broadcast_id)}
                            className={`p-4 border-l-4 ${BROADCAST_PRIORITY_COLORS[broadcast.priority]} cursor-pointer hover:bg-bgBase ${!isRead ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                          >
                            <div className="flex items-start justify-between">
                              <h4 className={`font-medium text-sm ${!isRead ? 'text-textDefault' : 'text-textSecondary'}`}>{broadcast.title}</h4>
                              {!isRead && <span className="w-2 h-2 bg-blue-500 rounded-full"></span>}
                            </div>
                            <p className="text-sm text-textSecondary mt-1 line-clamp-2">{broadcast.message}</p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-textSecondary">
                              {broadcast.sender_name && <span>{broadcast.sender_name}</span>}
                              <span>•</span>
                              <span>{formatDate(broadcast.created_at)}</span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                {/* FAQ Tab */}
                {activeTab === 'faq' && (
                  <FaqTab faqItems={faqItems} t={t} />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
